/* eslint-disable react-hooks/exhaustive-deps */
import { reportApis } from 'Api/reports';
import dayjs, { Dayjs } from 'dayjs';
import { TablePagination, TableSorter } from 'interfaces/global.interface';
import { useContext, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { AppDispatch } from 'redux/store';
import { CloseSvg } from 'utils/svgs';
import { minutesToTimeString, toastText } from 'utils/utils';
import { fetchPayPeriodAction } from '../../../redux/actions/payPeriodAction';
import AttendanceHeader from './attendanceReportHeader';
import AttendanceReportTable from './attendanceReportTable';
import styles from './index.module.scss';
import { fetchConstantDropdownAction } from '../../../redux/actions/constantDropdownAction';
import { PermissionContext } from 'components/Global/AuthLayout';

const AttendanceReportComponent = () => {
	const [selectedPayPeriod, setSelectedPayPeriod] = useState('');
	const [branchId, setBranchId] = useState('');
	const [departmentId, setDepartmentId] = useState('');
	const [year, setYear] = useState<Dayjs>(dayjs());
	const [currentPage, setCurrentPage] = useState(1);
	const [pageSize, setPageSize] = useState(31);
	const [sortField, setSortField] = useState('');
	const [sortOrder, setSortOrder] = useState('');
	const [totalRecords, setTotalRecords] = useState(0);
	const [ExportButtonLoading, setExportButtonLoading] = useState(false);

	//attendance
	const [attendanceRows, setAttendanceRows] = useState<any[]>([]);
	const [attendanceColumns, setAttendanceColumns] = useState<any[]>([]);
	const [attendanceLoading, setAttendanceLoading] = useState(false);

	const dispatch = useDispatch<AppDispatch>();

	const permissionsToCheck = [
		'View_Payroll_Reports',
		'View_Earning_and_Deduction_Reports',
		'View_Tax_Reports',
	];

	const { data: payPeriodData, isLoading } = useSelector(
		(state: any) => state.payPeriod
	);

	const { allowedPermissions } = useContext(PermissionContext);
	const navigationPermission = allowedPermissions.some((permission: string) =>
		permissionsToCheck.includes(permission)
	);

	const navigate = useNavigate();

	const exportAttendanceReport = async () => {
		setExportButtonLoading(true);
		try {
			const query: any = {
				branchId: branchId,
				departmentId: departmentId,
				payPeriodId: selectedPayPeriod,
				year: year.year(),
			};
			const response = await reportApis.exportAttendanceReport(query);

			// Find the label based on selectedPayPeriod
			const selectedPayPeriodData = payPeriodData.find(
				(period: any) => period.value === selectedPayPeriod
			);
			const label = selectedPayPeriodData
				? selectedPayPeriodData.label
				: '';

			const fileName = `${label}_attendance_report.csv`;

			const csvBlob = new Blob([response.data], {
				type: 'text/csv;charset=utf-8;',
			});
			const pdfUrl = URL.createObjectURL(csvBlob);

			const link = document.createElement('a');
			link.href = pdfUrl;
			link.setAttribute('download', fileName);
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);
		} catch (error: any) {
			let message = 'Something went wrong in fetching Attendance Report';

			if (error?.response?.data?.message) {
				message = error.response.data.message;
			}
			toastText(message, 'error');
		} finally {
			setExportButtonLoading(false);
		}
	};

	const exportButton = [
		{
			text: 'Export',
			isLoading: ExportButtonLoading,
			className: 'btn-blue',
			// icon: <ExportSvg />,
			isSubmit: true,
			onclick: () => {
				exportAttendanceReport();
			},
			disabled: ExportButtonLoading,
		},
	];

	const dropdownOptions = useSelector(
		(state: any) => state.constantDropdown.data
	);
	const onYearChange = (value: string) => {
		if (value) {
			const dayjsValue = dayjs(value);
			setYear(dayjsValue);
		} else {
			setYear(dayjs());
		}
		setCurrentPage(1);
	};
	const handleChange = (value: any, isBranch: boolean) => {
		if (isBranch) {
			setBranchId(value);
			setDepartmentId('');
		} else {
			setDepartmentId(value);
		}

		setCurrentPage(1);
	};

	const tableChangeHandler = (
		pagination: TablePagination,
		filters: any,
		sorter: TableSorter
	) => {
		setTotalRecords(pagination.total);
		setCurrentPage(pagination.current);
		setPageSize(pagination.pageSize);
		let sortOrderValue = '';
		if (sorter?.order === 'ascend') {
			sortOrderValue = 'asc';
		} else if (sorter?.order) {
			sortOrderValue = 'desc';
		}
		setSortOrder(sortOrderValue);
		setSortField(sorter.field);
	};

	const generateColumns = (data: any) => {
		const columns = data.map((item: any) => {
			let title = '';
			let fixed = '';
			let width = 100; // Default width for other columns

			if (item.trim() === 'empName') {
				title = 'Employee Name';
				fixed = 'left';
				width = 130;
			} else if (item.trim() === 'empCode') {
				title = 'Employee Code';
				fixed = 'left';
				width = 120;
			} else if (item.trim() === 'totalAbsentDays') {
				title = 'Absent Days';
				fixed = 'right';
				width = 70;
			} else if (item.trim() === 'totalRegularHours') {
				title = 'Regular Hours';
				fixed = 'right';
			} else if (item.trim() === 'totalWeekDayOvertime') {
				title = 'Weekday OT Hours';
				fixed = 'right';
			} else if (item.trim() === 'totalSundayOvertime') {
				title = 'Sunday OT Hours';
				fixed = 'right';
			} else if (item.trim() === 'totalShiftDifference') {
				title = 'Shift Diff Hours';
				fixed = 'right';
			} else {
				title = dayjs(item).format('DD');
				width = 70;
			}

			return {
				title: title,
				dataIndex: item,
				key: item,
				align: 'center',
				fixed: fixed,
				width: width,
				className: 'bg-white',
				render: (text: any) => {
					if (
						typeof text === 'number' &&
						text > 0 &&
						item !== 'totalWorkingHours' &&
						item !== 'totalAbsentDays'
					) {
						return (
							<span style={{ color: 'green' }}>
								{minutesToTimeString(text)}
							</span>
						);
					} else if (text === 0) {
						return <span style={{ color: 'red' }}>{text}</span>;
					} else {
						return <span>{text}</span>;
					}
				},
			};
		});
		return columns;
	};

	const fetchAttendanceData = async () => {
		try {
			setAttendanceLoading(true);
			if (!selectedPayPeriod) {
				setAttendanceRows([]);
				setAttendanceColumns([]);
				setTotalRecords(0);
				return;
			}
			const query: any = {
				page: currentPage,
				branchId: branchId,
				departmentId: departmentId,
				payPeriodId: selectedPayPeriod,
				year: year.year(),
				// sortBy: sortField,
				// sortOrder: sortOrder,
				pageSize: pageSize - 1,
			};
			const attendanceResponseData =
				await reportApis.getAttendanceReports(query);
			if (attendanceResponseData?.data?.data) {
				const response = attendanceResponseData.data.data;

				const columns = generateColumns(response.headers);

				setAttendanceRows(response.data);
				setAttendanceColumns(columns);
				setTotalRecords(attendanceResponseData.data.count);
			}
		} catch (error: any) {
			let message = 'Something went wrong in getting attendance Report';

			if (error?.response?.data?.message) {
				message = error.response.data.message;
			}
			toastText(message, 'error');
		} finally {
			setAttendanceLoading(false);
		}
	};

	useEffect(() => {
		fetchAttendanceData();
	}, [
		currentPage,
		branchId,
		departmentId,
		selectedPayPeriod,
		sortField,
		sortOrder,
		pageSize,
	]);

	useEffect(() => {
		if (payPeriodData?.length > 1) {
			setSelectedPayPeriod(payPeriodData[1].value);
		} else {
			setSelectedPayPeriod('');
			setAttendanceRows([]);
			setAttendanceColumns([]);
			setTotalRecords(0);
		}
	}, [payPeriodData]);

	useEffect(() => {
		setAttendanceRows([]);
		setAttendanceColumns([]);
		setTotalRecords(0);
		setSelectedPayPeriod('');
	}, [year]);

	useEffect(() => {
		dispatch(fetchPayPeriodAction({ year: year.year() }));
	}, [year]);

	useEffect(() => {
		dispatch(fetchConstantDropdownAction({}));
	}, []);

	return (
		<div className={styles['attendance-report']}>
			<div className={styles['attendance-report__header']}>
				<div className={styles['attendance-report__header__title']}>
					Attendance Report
				</div>
				<div
					className={styles['attendance-report__header__actions']}
					onClick={() => {
						navigationPermission
							? navigate('/reports')
							: navigate('/');
					}}
				>
					<CloseSvg />
				</div>
			</div>
			<div className={styles['attendance-report__table']}>
				{' '}
				<AttendanceHeader
					year={year}
					onYearChange={onYearChange}
					selectedPayPeriod={selectedPayPeriod}
					setSelectedPayPeriod={setSelectedPayPeriod}
					branchId={branchId}
					departmentId={departmentId}
					handleChange={handleChange}
					dropdownOptions={dropdownOptions}
					isLoading={isLoading}
					payPeriodData={payPeriodData}
					exportButton={exportButton}
					setCurrentPage={setCurrentPage}
				/>
				<AttendanceReportTable
					totalRecords={totalRecords}
					currentPage={currentPage}
					pageSize={pageSize}
					tableChangeHandler={tableChangeHandler}
					isLoading={attendanceLoading}
					rows={attendanceRows}
					columns={attendanceColumns}
				/>
			</div>
		</div>
	);
};

export default AttendanceReportComponent;
